/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/require-default-props */
// @flow
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import type { ElementRef, Node } from 'react';
import Button from '../Button';
import Drop from '../Drop';
import { useForwardedRef } from '../../core/refs';
import type { AlignType } from '../Drop';

const defaultDropAlign = { top: 'top', left: 'left' };
export type Props = {
  a11yTitle?: string,
  onAlign: ('top' | 'bottom') => void,
  disabled?: boolean,
  dropAlign?: AlignType,
  dropProps?: { align: AlignType },
  dropTarget: ElementRef<typeof HTMLElement>,
  id?: string | number,
  open?: boolean,
  label?: string,
  onClick?: () => void,
  onClose?: () => void,
  onOpen?: () => void,
  dropContent: Node,
};
const DropButton = forwardRef(
  (
    {
      a11yTitle = 'Open Drop',
      onAlign,
      disabled,
      dropAlign = defaultDropAlign,
      dropProps,
      dropContent,
      dropTarget,
      id,
      open,
      label,
      onClick,
      onClose,
      onOpen,
      ...rest
    }: Props,
    ref,
  ) => {
    const buttonRef = useForwardedRef(ref);
    const [show, setShow] = useState();
    useEffect(() => {
      if (open !== undefined && open !== show) {
        setShow(open);
      }
    }, [open, show]);
    const onDropClose = useCallback(
      (event) => {
        // if the user has clicked on our Button, don't do anything here,
        // handle that in onClickInternal() below.
        let node = event.target;
        while (node !== document && node !== buttonRef.current) {
          node = node.parentNode;
        }
        if (node !== buttonRef.current) {
          // don't change internal state if caller is driving
          if (open === undefined) setShow(false);
          if (onClose) onClose(event);
        }
      },
      [buttonRef, onClose, open],
    );

    const onClickInternal = useCallback(
      (event) => {
        if (!show) {
          setShow(true);
          if (onOpen) onOpen(event);
        } else {
          setShow(false);
          if (onClose) onClose(event);
        }
        if (onClick) onClick(event);
      },
      [onClick, onClose, onOpen, show],
    );

    return (
      <>
        <Button
          id={id}
          ref={buttonRef}
          a11yTitle={a11yTitle}
          disabled={disabled}
          label={label}
          {...rest}
          onClick={onClickInternal}
        />
        {show && buttonRef.current && (
          <Drop
            id={id ? `${id}__drop` : undefined}
            onAlign={onAlign}
            restrictFocus
            align={dropAlign}
            target={dropTarget || buttonRef.current}
            onClickOutside={onDropClose}
            onEsc={onDropClose}
            {...dropProps}
          >
            {dropContent}
          </Drop>
        )}
      </>
    );
  },
);

DropButton.displayName = 'DropButton';

export default DropButton;
